<template>
  <section class="project-periodic-notification-panel" aria-labelledby="project-periodic-notification-heading">
    <div class="periodic-heading">
      <div class="periodic-heading__title">
        <span class="periodic-heading__icon" aria-hidden="true">
          <CIcon name="cil-clock"/>
        </span>
        <div>
          <p class="periodic-kicker">PROJECT SCHEDULED SLACK</p>
          <h2 id="project-periodic-notification-heading">プロジェクト定期コメント通知</h2>
          <p>企業別休日カレンダーと種類別の指定時刻を使い、コメントをプロジェクトチャンネルへ送信します。</p>
        </div>
      </div>
      <div class="periodic-heading__badges">
        <CBadge :color="activeMemberCount > 0 ? 'info' : 'warning'">
          所属者 {{ activeMemberCount }}人
        </CBadge>
        <CBadge :color="mentionIssueUsers.length ? 'warning' : 'success'">
          個人メンション {{ mentionableUsers.length }}/{{ mentionUsers.length }}人
        </CBadge>
        <CBadge :color="enabledCount ? (deliverable ? 'success' : 'warning') : 'secondary'">
          {{ enabledCount ? `${enabledCount}件 ON${deliverable ? '' : '（送信条件未達）'}` : 'すべてOFF' }}
        </CBadge>
      </div>
    </div>

    <CAlert v-if="errorMessage" color="danger" close-button @update:show="errorMessage = ''">
      {{ errorMessage }}
    </CAlert>
    <CAlert v-if="successMessage" color="success" close-button @update:show="successMessage = ''">
      {{ successMessage }}
    </CAlert>

    <div v-if="loadingInitial" class="periodic-loading">
      <CSpinner color="primary"/>
      <span>定期コメント通知設定を読み込んでいます。</span>
    </div>

    <div v-else-if="!settingLoaded" class="periodic-load-failed">
      <CIcon name="cil-warning" size="2xl"/>
      <strong>定期コメント通知設定を表示できませんでした。</strong>
      <p>通信状態とプロジェクト管理権限を確認して、もう一度読み込んでください。</p>
      <CButton type="button" color="primary" variant="outline" @click="loadSetting">再読み込み</CButton>
    </div>

    <template v-else>
      <CAlert color="info">
        各タイミングはそれぞれON／OFFと送信時刻を設定できます。同じ時刻に複数の通知が重なっても、順番にすべて送信します。
      </CAlert>

      <CAlert v-if="enabledCount && !deliverable" color="warning">
        通知をONにした設定が{{ enabledCount }}件ありますが、{{ undeliverableReason }}
        この状態では自動送信しません。設定の保存とテスト送信はできます。
      </CAlert>
      <CAlert v-else-if="activeMemberCount === 0" color="warning">
        現在、このプロジェクトに有効な所属者はいません。所属者が0人の間は、休暇通知・定期コメント通知とも自動送信しません。
      </CAlert>

      <CAlert v-if="!slackFeatureEnabled" color="warning">
        現在の契約プランではSlack通知を利用できません。既存設定をすべてOFFへ戻す操作はできます。
      </CAlert>
      <CAlert v-else-if="!slackTokenConfigured" color="warning">
        自社設定にSlack Botトークンがありません。先に「会社管理 → 自社設定」でBotトークンを設定してください。既存設定をすべてOFFへ戻す操作はできます。
      </CAlert>

      <div class="shared-channel-card">
        <div>
          <p class="periodic-kicker">SHARED CHANNEL</p>
          <h3>送信先チャンネル</h3>
          <p>
            当日休暇通知と定期コメント通知は同じチャンネルへ送信します。
            チャンネルIDの変更は上の「プロジェクトSlack通知先」で行います。
          </p>
        </div>
        <div class="shared-channel-view">
          <span>現在の送信先</span>
          <strong v-if="hasValidChannel"><code>{{ sharedChannel }}</code></strong>
          <strong v-else class="shared-channel-view__missing">未設定</strong>
          <small v-if="!hasValidChannel">
            上の「プロジェクトSlack通知先」でSlackチャンネルIDを保存すると、定期コメント通知をONにできます。
          </small>
        </div>
      </div>

      <CAlert color="secondary" class="mention-guidance">
        <strong>Slackメンション：</strong>
        <code>@channel</code>、<code>@here</code>、<code>@everyone</code>は公開メンションとして動作します。
        個人メンションは、現在このプロジェクトに所属し、一意で有効なSlackメンバーIDが登録されている人だけ利用できます。
        他プロジェクト・旧所属者・未登録IDは通常文字として送信します。
      </CAlert>

      <CAlert v-if="mentionIssueUsers.length" color="warning" class="member-issue-alert">
        所属メンバーは全員表示していますが、{{ mentionIssueUsers.length }}人は個人メンションに利用できません。
        Slack ID未登録 {{ mentionStatusCounts.NOT_CONFIGURED }}人、形式要確認 {{ mentionStatusCounts.INVALID_FORMAT }}人、重複 {{ mentionStatusCounts.DUPLICATE }}人です。
        ユーザ管理または本人設定でSlackメンバーIDを修正してください。
      </CAlert>

      <div v-if="mentionUsers.length" class="member-mention-card">
        <div class="member-mention-card__heading">
          <div>
            <p class="periodic-kicker">PROJECT MEMBERS</p>
            <h3>現在の所属メンバー</h3>
            <p>Slack IDが未登録・不正・重複の人も、所属確認のため一覧から除外しません。</p>
          </div>
          <CBadge :color="mentionIssueUsers.length ? 'warning' : 'success'">
            {{ mentionIssueUsers.length ? `要確認 ${mentionIssueUsers.length}人` : '全員メンション可能' }}
          </CBadge>
        </div>
        <div class="member-mention-list">
          <div
            v-for="user in mentionUsers"
            :key="user.member_key"
            class="member-mention-item"
            :class="{ 'member-mention-item--issue': !user.mention_available }"
          >
            <div>
              <strong>{{ user.name }}</strong>
              <code v-if="user.slack_id">{{ user.slack_id }}</code>
              <small v-else>Slack ID未登録</small>
            </div>
            <CBadge :color="memberStatusColor(user.mention_status)">
              {{ memberStatusLabel(user.mention_status) }}
            </CBadge>
          </div>
        </div>
      </div>

      <section
        v-for="group in scheduleGroups"
        :key="group.key"
        class="schedule-group"
      >
        <div class="schedule-group__heading">
          <h3>{{ group.label }}</h3>
          <p>{{ group.description }}</p>
        </div>
        <div class="schedule-grid">
        <article
          v-for="schedule in group.items"
          :key="schedule.code"
          class="schedule-card"
          :class="{ 'schedule-card--enabled': form[schedule.flagField] }"
        >
          <header class="schedule-card__header">
            <div>
              <p class="periodic-kicker">{{ schedule.kicker }}</p>
              <h3>{{ schedule.label }}</h3>
              <p>{{ schedule.description }}</p>
            </div>
            <CBadge :color="form[schedule.flagField] ? (deliverable ? 'success' : 'warning') : 'secondary'">
              {{ form[schedule.flagField] ? (deliverable ? '通知ON' : '通知ON（送信対象外）') : '通知OFF' }}
            </CBadge>
          </header>

          <div class="schedule-control-row">
            <button
              :id="`periodic-switch-${schedule.code}`"
              type="button"
              class="periodic-switch"
              :class="{ 'periodic-switch--on': form[schedule.flagField] }"
              role="switch"
              :aria-checked="form[schedule.flagField] ? 'true' : 'false'"
              :aria-label="`${schedule.label}通知を${form[schedule.flagField] ? 'OFF' : 'ON'}にする`"
              :disabled="processing"
              @click.prevent.stop="toggleSchedule(schedule)"
            >
              <span class="periodic-switch__track" aria-hidden="true">
                <span class="periodic-switch__thumb"></span>
              </span>
              <span class="periodic-switch__text">
                <strong>{{ form[schedule.flagField] ? '通知ON' : '通知OFF' }}</strong>
                <small>クリックしてこのタイミングだけ切り替えます。</small>
              </span>
            </button>

            <div class="schedule-time-field">
              <label :for="`periodic-time-${schedule.code}`">送信時刻</label>
              <input
                :id="`periodic-time-${schedule.code}`"
                v-model="form[schedule.timeField]"
                type="time"
                class="form-control"
                min="00:00"
                max="23:59"
                step="60"
                :disabled="processing"
                :aria-invalid="scheduleErrors[schedule.code] ? 'true' : 'false'"
                @input="clearScheduleError(schedule.code)"
              >
            </div>
          </div>

          <label :for="`periodic-message-${schedule.code}`">通知コメント</label>
          <div class="mention-toolbar" :aria-label="`${schedule.label}のメンション挿入`">
            <span>挿入</span>
            <CButton
              v-for="publicMention in publicMentions"
              :key="`${schedule.code}-${publicMention.label}`"
              type="button"
              color="secondary"
              variant="outline"
              size="sm"
              :disabled="processing"
              @click="insertMention(schedule, publicMention.syntax)"
            >
              {{ publicMention.label }}
            </CButton>
            <select
              class="custom-select mention-user-select"
              :disabled="processing || mentionableUsers.length === 0"
              :aria-label="`${schedule.label}へ所属者メンションを挿入`"
              @change="insertSelectedMember(schedule, $event)"
            >
              <option value="">
                {{ mentionableUsers.length ? '所属者を選択' : 'メンション可能な所属者なし' }}
              </option>
              <option
                v-for="user in mentionUsers"
                :key="`${schedule.code}-${user.member_key}`"
                :value="user.member_key"
                :disabled="!user.mention_available"
              >
                {{ memberOptionLabel(user) }}
              </option>
            </select>
          </div>
          <textarea
            :id="`periodic-message-${schedule.code}`"
            v-model="form[schedule.messageField]"
            class="form-control schedule-message"
            rows="5"
            maxlength="1000"
            :disabled="processing"
            :placeholder="schedule.placeholder"
            :aria-invalid="scheduleErrors[schedule.code] ? 'true' : 'false'"
            @input="clearScheduleError(schedule.code)"
          ></textarea>
          <div class="schedule-message-meta">
            <p v-if="scheduleErrors[schedule.code]" class="periodic-error" role="alert">
              {{ scheduleErrors[schedule.code] }}
            </p>
            <small>{{ messageLength(schedule) }}/1000文字</small>
          </div>

          <CButton
            type="button"
            color="secondary"
            variant="outline"
            :disabled="processing"
            @click="sendTest(schedule)"
          >
            {{ testingCode === schedule.code ? '送信しています' : `${form[schedule.timeField]}の内容をテスト送信` }}
          </CButton>
        </article>
        </div>
      </section>

      <CAlert color="secondary" class="periodic-note">
        週初は週の重点共有、週最終は週報・振り返り、月末最終は勤怠・請求・締め作業の案内に利用できます。個人メンション候補は通知時点の現所属者から再確認されます。
      </CAlert>

      <div class="periodic-actions">
        <CButton
          type="button"
          color="primary"
          :disabled="processing"
          @click="saveSettings"
        >
          {{ savingSettings ? '保存しています' : 'プロジェクト定期コメント設定を保存' }}
        </CButton>
      </div>
    </template>
  </section>
</template>

<script>
import periodicApi from '@/services/projectPeriodicNotifications'
import leaveApi from '@/services/projectLeaveNotifications'

const CHANNEL_PATTERN = /^[CG][A-Z0-9]{8,30}$/
const SLACK_MEMBER_PATTERN = /^[UW][A-Z0-9]{8,30}$/
const TIME_PATTERN = /^(?:[01]\d|2[0-3]):[0-5]\d$/
const MENTION_STATUSES = ['AVAILABLE', 'NOT_CONFIGURED', 'INVALID_FORMAT', 'DUPLICATE']

// サーバがまだ配列を読まない期間、この3種だけは従来の項目名でも送る。
// 新しいタイミングはここへ足さない（配列で受け取ってもらう）。
const LEGACY_SCHEDULE_CODES = ['WEEK_START', 'WEEK_END', 'MONTH_END']

// 月次と週次を混ぜて並べると読み違えて設定しやすい。見出しで分けて出す。
const SCHEDULE_GROUPS = [
  {
    key: 'MONTHLY',
    label: '月次',
    description: '月の始まりと終わりに送ります。月初は提出の開始、月末は締めの案内に使います。'
  },
  {
    key: 'WEEKLY',
    label: '週次',
    description: '週の始まりと終わりに送ります。週初は重点の共有、週最終は実績と翌週予定の共有に使います。'
  }
]

const SCHEDULES = [
  {
    code: 'MONTH_START',
    group: 'MONTHLY',
    kicker: 'MONTH START',
    label: '月初営業日',
    defaultTime: '09:10',
    description: '毎月の最初の会社営業日に送信します。1日が休日なら次の営業日へ移動します。',
    placeholder: '例：先月分の勤務表・経費の提出をお願いします。',
    flagField: 'month_start_flg',
    timeField: 'month_start_time',
    messageField: 'month_start_message'
  },
  {
    code: 'WEEK_START',
    group: 'WEEKLY',
    kicker: 'WEEK START',
    label: '週初営業日',
    defaultTime: '09:05',
    description: '毎週の最初の会社営業日に送信します。月曜日が休日なら次の営業日へ移動します。',
    placeholder: '例：今週の重点事項と確認期限を共有してください。',
    flagField: 'week_start_flg',
    timeField: 'week_start_time',
    messageField: 'week_start_message'
  },
  {
    code: 'WEEK_END',
    group: 'WEEKLY',
    kicker: 'WEEK END',
    label: '週最終営業日',
    defaultTime: '17:25',
    description: '毎週の最後の会社営業日に送信します。金曜日が休日なら直前の営業日へ移動します。',
    placeholder: '例：今週の実績・課題・翌週予定の共有をお願いします。',
    flagField: 'week_end_flg',
    timeField: 'week_end_time',
    messageField: 'week_end_message'
  },
  {
    code: 'MONTH_END',
    group: 'MONTHLY',
    kicker: 'MONTH END',
    label: '月末最終営業日',
    defaultTime: '17:35',
    description: '毎月の最後の会社営業日に送信します。月末が休日なら直前の営業日へ移動します。',
    placeholder: '例：勤怠・業務報告・請求関連の月末確認をお願いします。',
    flagField: 'month_end_flg',
    timeField: 'month_end_time',
    messageField: 'month_end_message'
  }
]

export default {
  name: 'ProjectPeriodicSlackNotificationSettings',
  props: {
    projectId: {
      type: [Number, String],
      required: true
    },
    /** 上の「プロジェクトSlack通知先」が保存されるたびに親が増やす。 */
    channelVersion: {
      type: Number,
      default: 0
    }
  },
  data () {
    return {
      schedules: SCHEDULES,
      publicMentions: [
        { label: '@channel', syntax: '<!channel>' },
        { label: '@here', syntax: '<!here>' },
        { label: '@everyone', syntax: '<!everyone>' }
      ],
      loadingInitial: true,
      settingLoaded: false,
      savingSettings: false,
      testingCode: '',
      errorMessage: '',
      successMessage: '',
      scheduleErrors: {},
      slackFeatureEnabled: false,
      slackTokenConfigured: false,
      leaveNotificationFlg: false,
      activeMemberCount: 0,
      mentionUsers: [],
      sharedChannel: '',
      savedPeriodicEnabled: false,
      form: this.emptyForm()
    }
  },
  computed: {
    processing () {
      return this.savingSettings || Boolean(this.testingCode)
    },
    // 月次と週次に分けて出す。各グループ内は 始まり → 終わり の順。
    scheduleGroups () {
      return SCHEDULE_GROUPS
        .map(group => ({
          ...group,
          items: this.schedules.filter(schedule => schedule.group === group.key)
        }))
        .filter(group => group.items.length)
    },
    enabledCount () {
      return this.schedules.filter(schedule => this.form[schedule.flagField]).length
    },
    anyEnabled () {
      return this.enabledCount > 0
    },
    hasValidChannel () {
      return CHANNEL_PATTERN.test(this.sharedChannel)
    },
    /**
     * ONにしても自動送信されない条件があるので、ONとは別に出す。
     * Backendは契約Feature・Botトークン・送信先・有効な所属者のどれかが
     * 欠けていると、ログだけ残して送信せずに戻る。
     */
    deliverable () {
      return this.slackFeatureEnabled
        && this.slackTokenConfigured
        && this.hasValidChannel
        && this.activeMemberCount > 0
    },
    undeliverableReason () {
      if (this.deliverable) return ''
      if (!this.slackFeatureEnabled) return '現在の契約プランではSlack通知を利用できません。'
      if (!this.slackTokenConfigured) return '自社設定にSlack Botトークンがありません。'
      if (!this.hasValidChannel) return '送信先チャンネルが未設定です。'
      return '有効な所属者が0人です。'
    },
    mentionableUsers () {
      return this.mentionUsers.filter(user => user.mention_available && SLACK_MEMBER_PATTERN.test(user.slack_id))
    },
    mentionIssueUsers () {
      return this.mentionUsers.filter(user => !user.mention_available)
    },
    mentionStatusCounts () {
      const result = {
        AVAILABLE: 0,
        NOT_CONFIGURED: 0,
        INVALID_FORMAT: 0,
        DUPLICATE: 0
      }
      this.mentionUsers.forEach(user => {
        const status = MENTION_STATUSES.includes(user.mention_status)
          ? user.mention_status
          : 'INVALID_FORMAT'
        result[status] += 1
      })
      return result
    }
  },
  watch: {
    projectId () {
      this.loadSetting()
    },
    channelVersion () {
      if (this.settingLoaded) this.refreshSharedChannel()
    }
  },
  mounted () {
    this.loadSetting()
  },
  methods: {
    // タイミングを増やしても、この下を触らずに済むよう SCHEDULES から作る。
    /**
     * 応答からフォームを作る。
     *
     * <p>schedules 配列があればそれを使う。無ければ従来の項目名で読む
     * （画面とサーバの配置がずれている間の互換）。</p>
     */
    formFromResponse (value) {
      const form = this.emptyForm()
      const rows = Array.isArray(value.schedules) ? value.schedules : []
      SCHEDULES.forEach(schedule => {
        const row = rows.find(item => item && item.schedule_code === schedule.code)
        if (row) {
          form[schedule.flagField] = row.notification_flg === true
          form[schedule.timeField] = this.normalizedTime(row.notification_time, schedule.defaultTime)
          form[schedule.messageField] = row.message || ''
          return
        }
        form[schedule.flagField] = value[schedule.flagField] === true
        form[schedule.timeField] = this.normalizedTime(value[schedule.timeField], schedule.defaultTime)
        form[schedule.messageField] = value[schedule.messageField] || ''
      })
      return form
    },

    emptyForm () {
      const form = {}
      SCHEDULES.forEach(schedule => {
        form[schedule.flagField] = false
        form[schedule.timeField] = schedule.defaultTime
        form[schedule.messageField] = ''
      })
      return form
    },
    async loadSetting () {
      this.loadingInitial = true
      this.settingLoaded = false
      this.clearMessages()
      try {
        const values = await Promise.all([
          periodicApi.get(this.projectId),
          leaveApi.get(this.projectId)
        ])
        this.applyPeriodicResponse(values[0])
        this.applyLeaveResponse(values[1])
        this.settingLoaded = true
      } catch (error) {
        this.errorMessage = this.apiError(error, 'プロジェクト定期コメント通知設定を読み込めませんでした。')
      } finally {
        this.loadingInitial = false
      }
    },
    applyPeriodicResponse (response) {
      const value = response || {}
      this.slackFeatureEnabled = value.slack_feature_enabled === true
      this.slackTokenConfigured = value.slack_token_configured === true
      this.mentionUsers = this.normalizedMentionUsers(value.mention_users)
      this.activeMemberCount = Math.max(
        this.normalizedMemberCount(value.active_member_count),
        this.mentionUsers.length
      )
      this.form = this.formFromResponse(value)
      this.savedPeriodicEnabled = this.anyEnabled
      this.sharedChannel = this.normalizedChannel(value.slack_channel)
    },
    applyLeaveResponse (response) {
      const value = response || {}
      this.leaveNotificationFlg = value.notification_flg === true
      this.slackFeatureEnabled = value.slack_feature_enabled === true
      this.slackTokenConfigured = value.slack_token_configured === true
      if (value.active_member_count !== undefined && value.active_member_count !== null) {
        this.activeMemberCount = Math.max(
          this.normalizedMemberCount(value.active_member_count),
          this.mentionUsers.length
        )
      }
      this.sharedChannel = this.normalizedChannel(value.slack_channel)
    },
    /**
     * 送信先チャンネルは「プロジェクトSlack通知先」が正本。
     * 上のパネルが保存したら、入力中のスケジュールを消さずに送信先だけ取り直す。
     */
    async refreshSharedChannel () {
      try {
        this.applyLeaveResponse(await leaveApi.get(this.projectId))
      } catch (error) {
        this.errorMessage = this.apiError(error, '送信先チャンネルを読み直せませんでした。')
      }
    },
    normalizedMemberCount (value) {
      const count = Number(value)
      return Number.isFinite(count) && count > 0 ? Math.floor(count) : 0
    },
    normalizedMentionUsers (values) {
      if (!Array.isArray(values)) return []
      const mapped = values.map((value, index) => {
        const source = value || {}
        const slackId = this.canonicalSlackId(source.slack_id)
        const name = String(source.name || `所属者${index + 1}`).trim() || `所属者${index + 1}`
        const memberKey = String(source.user_id == null ? `${index}-${name}` : source.user_id)
        const requestedStatus = String(source.mention_status || '').trim().toUpperCase()
        return {
          member_key: memberKey,
          user_id: source.user_id,
          name,
          slack_id: slackId,
          backend_available: source.mention_available,
          requested_status: MENTION_STATUSES.includes(requestedStatus) ? requestedStatus : ''
        }
      })

      const validCounts = new Map()
      mapped.forEach(user => {
        if (!SLACK_MEMBER_PATTERN.test(user.slack_id)) return
        validCounts.set(user.slack_id, (validCounts.get(user.slack_id) || 0) + 1)
      })

      return mapped.map(user => {
        let status = user.requested_status
        if (!user.slack_id) {
          status = 'NOT_CONFIGURED'
        } else if (!SLACK_MEMBER_PATTERN.test(user.slack_id)) {
          status = 'INVALID_FORMAT'
        } else if ((validCounts.get(user.slack_id) || 0) > 1) {
          status = 'DUPLICATE'
        } else if (user.backend_available === false && status && status !== 'AVAILABLE') {
          // Backendが将来追加する安全上の利用不可状態を維持する。
        } else {
          status = 'AVAILABLE'
        }
        return {
          member_key: user.member_key,
          user_id: user.user_id,
          name: user.name,
          slack_id: user.slack_id,
          mention_available: status === 'AVAILABLE',
          mention_status: status
        }
      }).sort((first, second) => first.name.localeCompare(second.name, 'ja'))
    },
    canonicalSlackId (value) {
      let normalized = String(value || '').trim().toUpperCase()
      const wrapped = normalized.match(/^<@([UW][A-Z0-9]{8,30})>$/)
      if (wrapped) return wrapped[1]
      const prefixed = normalized.match(/^@([UW][A-Z0-9]{8,30})$/)
      if (prefixed) return prefixed[1]
      return normalized
    },
    memberStatusLabel (status) {
      const labels = {
        AVAILABLE: 'メンション可能',
        NOT_CONFIGURED: 'Slack ID未登録',
        INVALID_FORMAT: 'Slack ID形式要確認',
        DUPLICATE: 'Slack ID重複'
      }
      return labels[status] || 'Slack ID要確認'
    },
    memberStatusColor (status) {
      return status === 'AVAILABLE' ? 'success' : 'warning'
    },
    memberOptionLabel (user) {
      if (user.mention_available) return `${user.name}（${user.slack_id}）`
      return `${user.name}（${this.memberStatusLabel(user.mention_status)}${user.slack_id ? `：${user.slack_id}` : ''}）`
    },
    toggleSchedule (schedule) {
      this.$set(this.form, schedule.flagField, !this.form[schedule.flagField])
      this.clearScheduleError(schedule.code)
    },
    normalizedChannel (value) {
      return String(value || '')
        .replace(/\s/g, '')
        .toUpperCase()
        .slice(0, 50)
    },
    normalizedTime (value, fallback) {
      const normalized = String(value || '').trim()
      return TIME_PATTERN.test(normalized) ? normalized : fallback
    },
    /** 送信先はこの画面では編集しない。保存済みの値が使えるかどうかだけ見る。 */
    channelAvailable () {
      if (this.hasValidChannel) return true
      this.errorMessage = '先に上の「プロジェクトSlack通知先」でSlackチャンネルIDを保存してください。'
      return false
    },
    prerequisitesAvailable () {
      if (!this.slackFeatureEnabled) {
        this.errorMessage = '現在の契約プランではSlack通知を利用できません。'
        return false
      }
      if (!this.slackTokenConfigured) {
        this.errorMessage = '自社設定でSlack Botトークンを設定してください。'
        return false
      }
      return true
    },
    validateSchedule (schedule, requireMessage) {
      const notificationTime = String(this.form[schedule.timeField] || '').trim()
      if (!TIME_PATTERN.test(notificationTime)) {
        this.$set(this.scheduleErrors, schedule.code, '送信時刻を00:00から23:59までの範囲で入力してください。')
        return false
      }
      const message = this.normalizedMessage(this.form[schedule.messageField])
      if (message && message.length > 1000) {
        this.$set(this.scheduleErrors, schedule.code, '通知コメントは1000文字以内で入力してください。')
        return false
      }
      if (requireMessage && !message) {
        this.$set(this.scheduleErrors, schedule.code, '通知をONにする場合はコメントを入力してください。')
        return false
      }
      this.$delete(this.scheduleErrors, schedule.code)
      return true
    },
    validateAllSchedules () {
      let valid = true
      this.schedules.forEach(schedule => {
        if (!this.validateSchedule(schedule, this.form[schedule.flagField])) valid = false
      })
      return valid
    },
    normalizedMessage (value) {
      const normalized = String(value || '').trim()
      return normalized || null
    },
    /**
     * 保存する内容。
     *
     * <p>タイミングは schedules の配列で送る。増やしてもここを触らない。
     * 従来の項目名も併せて送るのは、サーバがまだ配列を読まない期間の互換
     * （新しいタイミングは配列にしか載らない）。</p>
     */
    periodicPayload () {
      const payload = {
        schedules: SCHEDULES.map(schedule => ({
          schedule_code: schedule.code,
          notification_flg: Boolean(this.form[schedule.flagField]),
          notification_time: this.form[schedule.timeField],
          message: this.normalizedMessage(this.form[schedule.messageField])
        }))
      }
      LEGACY_SCHEDULE_CODES.forEach(code => {
        const schedule = SCHEDULES.find(item => item.code === code)
        if (!schedule) return
        payload[schedule.flagField] = Boolean(this.form[schedule.flagField])
        payload[schedule.timeField] = this.form[schedule.timeField]
        payload[schedule.messageField] = this.normalizedMessage(this.form[schedule.messageField])
      })
      return payload
    },
    async saveSettings () {
      this.clearMessages(false)
      if (!this.validateAllSchedules()) return
      if (this.anyEnabled) {
        if (!this.prerequisitesAvailable()) return
        if (!this.channelAvailable()) return
      }

      this.savingSettings = true
      try {
        this.applyPeriodicResponse(await periodicApi.update(this.projectId, this.periodicPayload()))
        this.savedPeriodicEnabled = this.anyEnabled
        this.successMessage = this.anyEnabled
          ? 'プロジェクト定期コメント通知を保存しました。'
          : 'プロジェクト定期コメント通知をすべてOFFにしました。'
        this.$emit('updated', { project_id: Number(this.projectId), source: 'periodic' })
      } catch (error) {
        this.errorMessage = this.apiError(error, 'プロジェクト定期コメント通知設定を保存できませんでした。')
      } finally {
        this.savingSettings = false
      }
    },
    async sendTest (schedule) {
      this.clearMessages(false)
      if (!this.validateSchedule(schedule, true)) return
      if (!this.prerequisitesAvailable()) return
      if (!this.channelAvailable()) return

      this.testingCode = schedule.code
      try {
        await periodicApi.test(this.projectId, schedule.code, {
          notification_time: this.form[schedule.timeField],
          message: this.normalizedMessage(this.form[schedule.messageField])
        })
        this.successMessage = `${schedule.label}（${this.form[schedule.timeField]}）のコメントを共通プロジェクトチャンネルへテスト送信しました。`
      } catch (error) {
        this.errorMessage = this.apiError(
          error,
          'テスト通知を送信できませんでした。Botのチャンネル参加とチャンネルIDを確認してください。'
        )
      } finally {
        this.testingCode = ''
      }
    },
    insertSelectedMember (schedule, event) {
      const select = event && event.target
      const memberKey = select ? String(select.value || '') : ''
      const member = this.mentionUsers.find(user => user.member_key === memberKey)
      const slackId = member ? String(member.slack_id || '').trim().toUpperCase() : ''
      if (member && member.mention_available && SLACK_MEMBER_PATTERN.test(slackId)) {
        this.insertMention(schedule, `<@${slackId}>`)
      }
      if (select) select.value = ''
    },
    insertMention (schedule, syntax) {
      const field = schedule.messageField
      const current = String(this.form[field] || '')
      const textarea = this.$el.querySelector(`#periodic-message-${schedule.code}`)
      const start = textarea && Number.isInteger(textarea.selectionStart) ? textarea.selectionStart : current.length
      const end = textarea && Number.isInteger(textarea.selectionEnd) ? textarea.selectionEnd : start
      const before = current.slice(0, start)
      const after = current.slice(end)
      const leftSpace = before && !/\s$/.test(before) ? ' ' : ''
      const rightSpace = after && !/^\s/.test(after) ? ' ' : ''
      const inserted = leftSpace + syntax + rightSpace
      const next = before + inserted + after
      if (next.length > 1000) {
        this.$set(this.scheduleErrors, schedule.code, '通知コメントは1000文字以内で入力してください。')
        return
      }
      this.$set(this.form, field, next)
      this.clearScheduleError(schedule.code)
      const cursor = before.length + inserted.length
      this.$nextTick(() => {
        const target = this.$el.querySelector(`#periodic-message-${schedule.code}`)
        if (!target) return
        target.focus()
        if (typeof target.setSelectionRange === 'function') target.setSelectionRange(cursor, cursor)
      })
    },
    messageLength (schedule) {
      return String(this.form[schedule.messageField] || '').length
    },
    clearScheduleError (code) {
      this.$delete(this.scheduleErrors, code)
      this.errorMessage = ''
      this.successMessage = ''
    },
    clearMessages (clearScheduleErrors = true) {
      this.errorMessage = ''
      this.successMessage = ''
      if (clearScheduleErrors) this.scheduleErrors = {}
    },
    apiError (error, fallback) {
      const response = error && error.response
      const data = response && response.data
      if (typeof data === 'string' && data.trim()) return data.trim()
      if (data && typeof data.message === 'string' && data.message.trim()) return data.message.trim()
      return fallback
    }
  }
}
</script>

<style scoped>
.project-periodic-notification-panel {
  margin-top: 2rem;
  padding: 1.5rem;
  border: 1px solid #d8dbe0;
  border-radius: 0.5rem;
  background: #f8f9fa;
}
.periodic-heading,
.periodic-heading__title,
.periodic-heading__badges,
.periodic-loading,
.periodic-actions,
.mention-toolbar,
.schedule-control-row,
.periodic-switch,
.member-mention-card__heading,
.member-mention-item {
  display: flex;
}
.periodic-heading,
.member-mention-card__heading {
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.25rem;
}
.periodic-heading__title {
  align-items: flex-start;
  gap: 0.875rem;
}
.periodic-heading__badges {
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.45rem;
}
.periodic-heading__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.75rem;
  height: 2.75rem;
  flex: 0 0 2.75rem;
  border-radius: 50%;
  background: #f3e8ff;
  color: #6f42c1;
}
.periodic-heading h2,
.shared-channel-card h3,
.schedule-card h3,
.member-mention-card h3 {
  margin: 0;
}
.periodic-heading p,
.shared-channel-card p,
.schedule-card__header p,
.member-mention-card__heading p {
  margin: 0.35rem 0 0;
  color: #5c6873;
}
.periodic-kicker {
  margin: 0 0 0.15rem !important;
  color: #768192 !important;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.12em;
}
.periodic-loading {
  align-items: center;
  gap: 0.75rem;
  min-height: 5rem;
}
.periodic-load-failed {
  display: flex;
  min-height: 6rem;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  text-align: center;
}
.periodic-load-failed p {
  margin: 0;
  color: #5c6873;
}
.shared-channel-view {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0.9rem 1rem;
  border: 1px solid #e4e7ea;
  border-radius: 0.4rem;
  background: #f8f9fa;
}
.shared-channel-view span {
  color: #768192;
  font-size: 0.78rem;
}
.shared-channel-view strong {
  margin-top: 0.25rem;
  font-size: 1rem;
  overflow-wrap: anywhere;
}
.shared-channel-view__missing {
  color: #b1802b;
}
.shared-channel-view small {
  margin-top: 0.4rem;
  color: #768192;
  line-height: 1.45;
}
.shared-channel-card {
  display: grid;
  grid-template-columns: minmax(14rem, 0.8fr) minmax(18rem, 1.2fr);
  gap: 1.25rem;
  margin: 1.25rem 0;
  padding: 1.25rem;
  border: 1px solid #d8dbe0;
  border-radius: 0.45rem;
  background: #fff;
}
.schedule-card > label,
.schedule-time-field label {
  display: block;
  margin-bottom: 0.4rem;
  font-weight: 600;
}
.schedule-time-field small {
  display: block;
  margin-top: 0.35rem;
  color: #768192;
  line-height: 1.45;
}
.mention-guidance,
.member-issue-alert {
  line-height: 1.55;
}
.member-mention-card {
  margin: 1rem 0 1.25rem;
  padding: 1rem;
  border: 1px solid #d8dbe0;
  border-radius: 0.45rem;
  background: #fff;
}
.member-mention-list {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.6rem;
}
.member-mention-item {
  min-width: 0;
  align-items: center;
  justify-content: space-between;
  gap: 0.6rem;
  padding: 0.7rem;
  border: 1px solid #ebedef;
  border-radius: 0.35rem;
  background: #f8f9fa;
}
.member-mention-item--issue {
  border-color: #f9b115;
  background: #fff8e6;
}
.member-mention-item > div {
  min-width: 0;
}
.member-mention-item strong,
.member-mention-item code,
.member-mention-item small {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.member-mention-item code,
.member-mention-item small {
  margin-top: 0.2rem;
}
.schedule-group {
  margin-top: 1.5rem;
}
.schedule-group__heading {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #d8dbe0;
  margin-bottom: 1rem;
}
.schedule-group__heading h3 {
  margin: 0;
  font-size: 1.05rem;
}
.schedule-group__heading p {
  margin: 0;
  color: #768192;
  font-size: 0.85rem;
}
/* 1グループ2枚。件数が増えても余りが出ないよう2列で折り返す。 */
.schedule-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}
.schedule-card {
  display: flex;
  min-width: 0;
  flex-direction: column;
  padding: 1.15rem;
  border: 1px solid #d8dbe0;
  border-radius: 0.45rem;
  background: #fff;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}
.schedule-card--enabled {
  border-color: #2eb85c;
  box-shadow: 0 0 0 1px rgba(46, 184, 92, 0.12);
}
.schedule-card__header {
  display: flex;
  min-height: 8.2rem;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.65rem;
}
.schedule-control-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(7.5rem, 0.55fr);
  align-items: stretch;
  gap: 0.75rem;
  margin: 0.9rem 0 1rem;
}
.periodic-switch {
  width: 100%;
  min-height: 4.4rem;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem;
  border: 1px solid #d8dbe0;
  border-radius: 0.4rem;
  background: #f2f4f7;
  color: #3c4b64;
  text-align: left;
  cursor: pointer;
  appearance: none;
}
.periodic-switch:hover:not(:disabled) {
  border-color: #9da5b1;
}
.periodic-switch--on {
  border-color: #2eb85c;
  background: rgba(46, 184, 92, 0.08);
}
.periodic-switch:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}
.periodic-switch__track {
  position: relative;
  width: 3rem;
  height: 1.55rem;
  flex: 0 0 3rem;
  border-radius: 999px;
  background: #9da5b1;
}
.periodic-switch--on .periodic-switch__track {
  background: #2eb85c;
}
.periodic-switch__thumb {
  position: absolute;
  top: 0.2rem;
  left: 0.2rem;
  width: 1.15rem;
  height: 1.15rem;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
  transition: transform 0.2s ease;
}
.periodic-switch--on .periodic-switch__thumb {
  transform: translateX(1.45rem);
}
.periodic-switch:focus-visible {
  outline: 3px solid rgba(50, 31, 219, 0.25);
  outline-offset: 2px;
}
.periodic-switch__text {
  display: flex;
  min-width: 0;
  flex-direction: column;
}
.periodic-switch__text small,
.schedule-message-meta small {
  color: #768192;
}
.schedule-time-field {
  padding: 0.6rem;
  border: 1px solid #d8dbe0;
  border-radius: 0.4rem;
  background: #fff;
}
.mention-toolbar {
  flex-wrap: wrap;
  align-items: center;
  gap: 0.4rem;
  margin-bottom: 0.55rem;
  padding: 0.55rem;
  border: 1px solid #ebedef;
  border-radius: 0.35rem;
  background: #f8f9fa;
}
.mention-toolbar > span {
  color: #5c6873;
  font-size: 0.78rem;
  font-weight: 700;
}
.mention-user-select {
  min-width: 15rem;
  height: calc(1.5em + 0.5rem + 2px);
  margin-left: auto;
  padding-top: 0.25rem;
  padding-bottom: 0.25rem;
  font-size: 0.8rem;
}
.schedule-message {
  min-height: 8rem;
  resize: vertical;
}
.schedule-message-meta {
  display: flex;
  min-height: 2rem;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.5rem;
  margin-top: 0.35rem;
}
.schedule-message-meta small {
  margin-left: auto;
  white-space: nowrap;
}
.periodic-error {
  margin: 0.35rem 0 0;
  color: #e55353;
  font-size: 0.875rem;
}
.schedule-card .btn:last-child {
  margin-top: auto;
}
.periodic-note {
  margin-top: 1rem;
}
.periodic-actions {
  justify-content: flex-end;
  margin-top: 1.25rem;
}
@media (max-width: 1199.98px) {
  .schedule-grid { grid-template-columns: 1fr; }
  .schedule-card__header { min-height: auto; }
  .member-mention-list { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@media (max-width: 767.98px) {
  .shared-channel-card { grid-template-columns: 1fr; }
  .schedule-control-row { grid-template-columns: 1fr; }
  .mention-user-select { width: 100%; margin-left: 0; }
  .member-mention-list { grid-template-columns: 1fr; }
}
@media (max-width: 575.98px) {
  .project-periodic-notification-panel { padding: 1rem; }
  .periodic-heading,
  .member-mention-card__heading { flex-direction: column; }
  .periodic-heading__badges { justify-content: flex-start; }
  .periodic-actions .btn,
  .schedule-card > .btn { width: 100%; }
}
</style>
